import { useCallback, useState } from "react";

import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";
import { CustomColumnTypes, UpdateType } from "@/enum/list.enum";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { ColumnConfig } from "../../../../List/schema/columnConfig";

import { buildSnippetToken } from "../utils/snippetTokens";
import { templateRowKey } from "../utils/snippetRows";

import { valuesFromColumn } from "./useAiSnippetForm";
import { useSnippetPersistence } from "./useSnippetPersistence";

/**
 * Builds the PATCH payload that adds a column template to a list as a regular
 * AI snippet custom column (mirrors the modal's buildPayload shape).
 */
export const buildTemplateColumnPayload = (
	template: ColumnConfig,
	listId: number,
	prospectIds: number[]
) => {
	const values = valuesFromColumn(template);

	return {
		customColumnType: CustomColumnTypes.AI_SNIPPET,
		customColumnName: template.name ?? "",
		online: false,
		saveAsTemplate: false,
		templateId: template.id,
		value: {
			prompt: values.prompt,
			promptType: values.promptType,
			allowedValues:
				values.promptType === AiSnippetEnum.SCHEMA
					? values.allowedValues.filter((v) => v.trim() !== "")
					: [],
			fewShotExamples: values.fewShotExamples.filter(
				(e) => e.leadContext.trim() !== "" && e.idealOutput.trim() !== ""
			),
		},
		updateType: UpdateType.CUSTOM_COLUMN,
		prospectIds,
		id: listId,
	};
};

interface UseTemplateColumnInsertArgs {
	listId?: number;
	/** Snapshot the editor cursor before the async create starts. */
	captureCurrentPosition: () => void;
	/** Inserts the token at the cursor (with the parent's append fallback). */
	onInsertToken: (token: string) => boolean;
}

/**
 * Inserts a company-wide template into the message: first persists it as a
 * custom column on the list (so a real column id exists), then inserts the
 * `{column_<id>_<name>|<name>}` token. Tracks the in-flight row and the
 * columns created this session so the UI can flip template rows immediately,
 * before the list refetch lands in the slice.
 */
export const useTemplateColumnInsert = ({
	listId,
	captureCurrentPosition,
	onInsertToken,
}: UseTemplateColumnInsertArgs) => {
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const { persist } = useSnippetPersistence(listId);

	const [pendingKey, setPendingKey] = useState<string | null>(null);
	const [createdColumns, setCreatedColumns] = useState<ColumnConfig[]>([]);

	const insertTemplate = useCallback(
		async (template: ColumnConfig): Promise<void> => {
			if (pendingKey !== null || listId == null) {
				return;
			}

			captureCurrentPosition();
			setPendingKey(templateRowKey(template));

			try {
				const prospectIds = inbox[currentLeadIndex]?.id ? [inbox[currentLeadIndex].id] : [];
				const result = await persist(buildTemplateColumnPayload(template, listId, prospectIds));

				// Snippet placeholders are resolved by column id, so without the real
				// id we must not emit a token at all (it would never resolve at send
				// time).
				if (!result.ok || result.updatedColumn?.id == null) {
					if (result.ok) {
						console.error("Could not resolve the created AI snippet column id; skipping insert");
					}

					return;
				}

				const column = result.updatedColumn;

				setCreatedColumns((previous) => [...previous, column]);
				onInsertToken(buildSnippetToken(column.id as number, column.name ?? ""));
			} finally {
				setPendingKey(null);
			}
		},
		[pendingKey, listId, captureCurrentPosition, inbox, currentLeadIndex, persist, onInsertToken]
	);

	return { insertTemplate, pendingKey, createdColumns };
};
